import { createContext, useContext } from "react";

import { Schemas } from "../../../types";
import { VERIFICATION_TYPE } from "../constants";
import { EMPLOYMENT_VERIFICATION_TEMPLATE_CONFIG } from "../utils";
import { EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG } from "../../experience-verification-templates/utils";

export type EmploymentVerificationFormCheckArguments =
  Schemas["CheckArgumentsCreate"] & {
    fca_regulated_references_enabled?: boolean;
  };

export type VerificationTemplate = Omit<
  Schemas["EmploymentVerificationTemplateCreate"],
  "check_arguments"
> & {
  check_arguments: EmploymentVerificationFormCheckArguments;
};

export const initialVerificationTemplateValues: VerificationTemplate = {
  name: "",
  check_arguments: {
    validation_type: VERIFICATION_TYPE.quantity,
    minimum: EMPLOYMENT_VERIFICATION_TEMPLATE_CONFIG.DEFAULT_MINIMUM_EMPLOYERS,
    maximum: EMPLOYMENT_VERIFICATION_TEMPLATE_CONFIG.DEFAULT_MAXIMUM_EMPLOYERS,
    years: EMPLOYMENT_VERIFICATION_TEMPLATE_CONFIG.DEFAULT_YEARS,
    maximum_gap_period_in_days: 180,
    gap_threshold_unit: "MONTHS",
    minimum_gap_period_months:
      EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.DEFAULT_MINIMUM_GAP_PERIOD_MONTHS,
    minimum_gap_period_days: undefined,
    include_gap_detection: true,
    gap_verification_type: "STATEMENT",
    reference_rule_options: undefined,
    document_collection_mode: undefined,
    fca_firm_reference_number: "",
    digital_verification: true,
  },
};

export interface VerificationTemplatesContextType {
  verificationTemplate: VerificationTemplate;
  setVerificationTemplate: (verificationTemplate: VerificationTemplate) => void;
  isLastStep: boolean;
  numberOfSteps: number;
  currentStep: number;
  goToNextStep: () => void;
  goToPrevStep: () => void;
}

export const VerificationTemplatesContext =
  createContext<VerificationTemplatesContextType>({
    verificationTemplate: initialVerificationTemplateValues,
    setVerificationTemplate: (newState: VerificationTemplate) => newState,
    isLastStep: false,
    numberOfSteps: 0,
    currentStep: 0,
    goToNextStep: () => {},
    goToPrevStep: () => {},
  });

export function useVerificationTemplatesContext() {
  return useContext(VerificationTemplatesContext);
}
